import { useState, useEffect, useRef } from 'react';
import {
  Send, Bot, User, RefreshCw, CheckCircle,
  AlertTriangle, ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import { buildApiUrl } from '../../services/apiConfig';

const TIER_LABELS = { T1: 'T1 — High Risk', T2: 'T2 — Pre-Certified', T3: 'T3 — Low Risk' };
const STEPS = [
  'welcome','collect_usecase','collect_data','collect_decision',
  'collect_exposure','collect_roi','classify','confirm','generate','complete',
];

function renderText(content) {
  if (!content) return null;
  return content.split('\n').map((line, i) => {
    if (!line) return <br key={i} />;
    const html = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    if (line.startsWith('- ') || line.startsWith('• '))
      return <li key={i} className="ml-4 list-disc" dangerouslySetInnerHTML={{ __html: html.slice(2) }} />;
    return <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function GovernanceIntake() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [state, setState] = useState('welcome');
  const [classification, setClassification] = useState(null);
  const [artefacts, setArtefacts] = useState(null);
  const [activeTab, setActiveTab] = useState('evidence_pack');
  const [expandedSections, setExpandedSections] = useState({});
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { startSession(); }, []);

  const startSession = async () => {
    setMessages([]); setClassification(null); setArtefacts(null); setState('welcome'); setLoading(true);
    try {
      const res = await fetch(buildApiUrl('NOVA_INGESTION', '/governance/intake/start'), { method: 'POST' });
      const data = await res.json();
      setSessionId(data.session_id);
      if (data.state) setState(data.state);
      setMessages([{ role: 'assistant', content: data.message }]);
    } catch {
      setMessages([{ role: 'assistant', content: "Welcome! I'm the AI Governance Intake Agent. Tell me your name, team, and the name of your AI initiative to begin." }]);
    } finally { setLoading(false); }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim(); setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl('NOVA_INGESTION', '/governance/intake/chat'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userMsg }),
      });
      const data = await res.json();
      if (data.state) setState(data.state);
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (data.classification) setClassification(data.classification);
      if (data.artefacts) setArtefacts(data.artefacts);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally { setLoading(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const currentStepIdx = STEPS.indexOf(state);

  return (
    <div className="flex flex-col h-full">
      <p className="px-6 pt-4 pb-0 text-[0.8rem] italic text-gray-600">Register AI initiatives — model card, risk tier, regulatory mapping, and evidence pack generation</p>
      <div className="flex-1 p-4 overflow-hidden">
        <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Progress bar */}
      <div className="border-b border-gray-200 px-6 py-2 shrink-0">
        <div className="flex gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-1 rounded-full transition-all duration-300 ${s === state ? 'bg-primary' : i < currentStepIdx ? 'bg-primary/40' : 'bg-gray-200'}`} />
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Chat panel */}
        <div className={`flex flex-col bg-white border-r border-gray-200 transition-all duration-500 ${artefacts ? 'w-5/12' : 'w-full max-w-2xl mx-auto'}`}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
            <div>
              <p className="text-sm font-semibold text-gray-900">AI Governance Intake Agent</p>
              <p className="text-xs text-gray-500">
                {state === 'complete'
                  ? <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" /> Intake complete</span>
                  : `Step: ${(state || 'welcome').replace(/_/g, ' ')}`}
              </p>
            </div>
            <button onClick={startSession} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
              <RefreshCw className="h-3 w-3" /> New session
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-primary' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
                  {msg.role === 'assistant' ? <Bot className="h-4 w-4 text-white" /> : <User className="h-4 w-4 text-white" />}
                </div>
                <div className={`max-w-[80%] p-3 rounded-lg text-sm ${msg.role === 'assistant' ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-none' : 'bg-primary text-white rounded-br-none'}`}>
                  {renderText(msg.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="px-5 py-4 border-t border-gray-200 bg-white shrink-0">
            <div className="flex gap-3 items-end">
              <textarea
                value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
                placeholder="Type your response..." rows={2}
                disabled={loading || state === 'complete'}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none disabled:opacity-50"
              />
              <button
                onClick={sendMessage} disabled={loading || !input.trim() || state === 'complete'}
                className="px-4 py-3 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:opacity-40 transition-colors flex items-center gap-1.5 shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">Enter to send · Shift+Enter for new line</p>
          </div>
        </div>

        {/* Artefacts panel */}
        {artefacts && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {classification && (
              <div className="px-5 py-3 border-b border-gray-200 bg-blue-50/50 flex items-center gap-4 flex-wrap shrink-0">
                <span className="text-sm text-gray-700">Archetype: <strong>{classification.archetype}</strong></span>
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primaryLight text-primary border border-primary/20">
                  {TIER_LABELS[classification.risk_tier] || classification.risk_tier}
                </span>
                {(classification.regulatory_flags || []).map(r => (
                  <span key={r} className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{r}</span>
                ))}
                {classification.estimated_approval_weeks && (
                  <span className="ml-auto text-xs text-gray-500">Est. approval: <strong className="text-gray-800">{classification.estimated_approval_weeks} weeks</strong></span>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Pipeline entry */}
              {artefacts.pipeline_entry && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Governance Pipeline</h3>
                    <span className="text-xs font-mono text-primary bg-primaryLight px-2 py-1 rounded">{artefacts.pipeline_entry.pipeline_id}</span>
                  </div>
                  <div className="px-4 py-3">
                    <p className="font-semibold text-gray-900 mb-1">{artefacts.pipeline_entry.initiative_name}</p>
                    <p className="text-xs text-gray-500 mb-3">{artefacts.pipeline_entry.lifecycle_stage}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(artefacts.pipeline_entry.approval_path || []).map((step, i, arr) => (
                        <span key={step.step} className="flex items-center gap-1.5">
                          <span className={`text-xs px-3 py-1 rounded-full font-medium ${step.status === 'DONE' ? 'bg-primary text-white' : step.status === 'DRAFTED' ? 'bg-primaryLight text-primary border border-primary/30' : 'bg-gray-100 text-gray-500'}`}>
                            {step.name}
                          </span>
                          {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Artefact tabs */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="flex border-b border-gray-200">
                  {[['evidence_pack', 'Evidence Pack'], ['model_card', 'Model Card'], ['monitoring_plan', 'Monitoring Plan']].map(([key, label]) => (
                    <button key={key} onClick={() => setActiveTab(key)}
                      className={`flex-1 px-3 py-3 text-xs font-medium transition-colors ${activeTab === key ? 'bg-primaryLight text-primary border-b-2 border-primary' : 'text-gray-500 hover:bg-gray-50'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="p-4 max-h-[500px] overflow-y-auto">
                  {activeTab === 'evidence_pack' && artefacts.evidence_pack && (
                    <div className="space-y-2">
                      {(artefacts.evidence_pack.evidence_items || []).map((item, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-100 rounded-lg">
                          {item.auto_generated
                            ? <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                            : <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800">{item.item}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {item.auto_generated ? 'Agent-drafted' : `Owner: ${item.owner}`}
                              {item.due_date && ` · Due: ${item.due_date}`}
                            </p>
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primaryLight text-primary shrink-0">{item.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {activeTab === 'model_card' && artefacts.model_card && (
                    <div className="space-y-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 mb-3">
                        Draft — generated from intake answers. Requires human review before submission.
                      </div>
                      {Object.entries(artefacts.model_card).map(([sectionKey, sectionVal]) => {
                        if (!sectionVal) return null;
                        const expanded = expandedSections[sectionKey] !== false;
                        return (
                          <div key={sectionKey} className="border border-gray-200 rounded-lg overflow-hidden">
                            <button onClick={() => toggleSection(sectionKey)} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                              <span className="text-sm font-medium text-gray-700 capitalize">{sectionKey.replace(/_/g, ' ')}</span>
                              {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                            </button>
                            {expanded && (
                              <table className="w-full text-xs">
                                <tbody>
                                  {Object.entries(sectionVal).map(([k, v]) => (
                                    <tr key={k} className="border-t border-gray-100">
                                      <td className="py-2 px-4 w-36 text-[10px] font-semibold text-gray-400 uppercase tracking-wide align-top whitespace-nowrap">{k.replace(/_/g, ' ')}</td>
                                      <td className="py-2 px-4 text-gray-700 align-top break-words">{Array.isArray(v) ? (v.length ? v.join(', ') : 'None') : String(v)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {activeTab === 'monitoring_plan' && artefacts.monitoring_plan && (
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(artefacts.monitoring_plan, null, 2)}</pre>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
